import * as Tone from "tone";

// Notification sound utility for classroom chat
export class NotificationSoundManager {
  private static instance: NotificationSoundManager | null = null;
  private isEnabled: boolean = true;
  private isInitialized: boolean = false;

  static getInstance(): NotificationSoundManager {
    if (!NotificationSoundManager.instance) {
      NotificationSoundManager.instance = new NotificationSoundManager();
    }

    return NotificationSoundManager.instance;
  }

  private constructor() {
    // Initialize Tone.js on first user interaction
    this.initializeTone();
  }

  private initializeTone() {
    const initTone = async () => {
      if (!this.isInitialized) {
        try {
          await Tone.start();
          this.isInitialized = true;
          console.log("[NotificationSound] Tone.js initialized successfully");
          document.removeEventListener("click", initTone);
          document.removeEventListener("keydown", initTone);
        } catch (error) {
          console.error("[NotificationSound] Error initializing Tone.js:", error);
        }
      }
    };

    document.addEventListener("click", initTone, { once: true });
    document.addEventListener("keydown", initTone, { once: true });
  }

  /**
   * Play a notification sound similar to Slack's knock brush
   */
  async playNotification(): Promise<void> {
    console.log("[NotificationSound] Attempting to play notification...");
    console.log("[NotificationSound] Enabled:", this.isEnabled);
    console.log("[NotificationSound] Initialized:", this.isInitialized);

    if (!this.isEnabled) {
      console.warn("[NotificationSound] Notifications are disabled");

      return;
    }

    if (!this.isInitialized) {
      console.warn("[NotificationSound] Tone.js not initialized - user interaction required");

      return;
    }

    try {
      console.log("[NotificationSound] Creating Slack-like knock brush sound...");

      // Create the brushy sound (noise with filter)
      const brushNoise = new Tone.Noise("pink");
      const brushFilter = new Tone.Filter({
        frequency: 800,
        type: "lowpass",
        rolloff: -12,
      });
      const brushGain = new Tone.Gain(0.3);

      brushNoise.connect(brushFilter);
      brushFilter.connect(brushGain);
      brushGain.toDestination();

      // Create the 3 distinct pops
      const popSynth = new Tone.Synth({
        oscillator: {
          type: "sine",
        },
        envelope: {
          attack: 0.01,
          decay: 0.1,
          sustain: 0,
          release: 0.1,
        },
      }).toDestination();

      const popGain = new Tone.Gain(0.4);

      popSynth.connect(popGain);

      // Play the brushy sound first
      brushNoise.start();
      brushGain.gain.setValueAtTime(0, 0);
      brushGain.gain.linearRampToValueAtTime(0.3, 0.05);
      brushGain.gain.exponentialRampToValueAtTime(0.01, 0.3);
      brushNoise.stop(0.3);

      // Play 3 distinct pops with slight delays
      const popFrequencies = [440, 523, 659]; // A, C, E notes
      const popTimings = [0.1, 0.2, 0.3]; // Slight delays between pops

      popTimings.forEach((time, index) => {
        popSynth.triggerAttackRelease(popFrequencies[index], "8n", `+${time}`);
      });

      console.log("[NotificationSound] ✅ Slack-like notification sound played successfully");
    } catch (error) {
      console.error("[NotificationSound] ❌ Error playing notification sound:", error);
    }
  }

  /**
   * Enable or disable notification sounds
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Check if notification sounds are enabled
   */
  isNotificationEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Check if Tone.js is initialized
   */
  isToneInitialized(): boolean {
    return this.isInitialized;
  }
}

// Export the instance for convenience
export const notificationSoundManager = NotificationSoundManager.getInstance();

// Convenience function for playing notification
export function playNotificationSound(): void {
  notificationSoundManager.playNotification();
}

// Function to enable/disable notifications
export function setNotificationSoundEnabled(enabled: boolean): void {
  notificationSoundManager.setEnabled(enabled);
}

// Function to check if notifications are enabled
export function isNotificationSoundEnabled(): boolean {
  return notificationSoundManager.isNotificationEnabled();
}

// Debug function to test notification sound
export function testNotificationSound(): void {
  console.log("[NotificationSound] Testing Slack-like notification sound...");
  notificationSoundManager.playNotification();
}

// Debug function to get notification manager status
export function getNotificationSoundStatus(): {
  enabled: boolean;
  toneInitialized: boolean;
} {
  const manager = NotificationSoundManager.getInstance();

  return {
    enabled: manager.isNotificationEnabled(),
    toneInitialized: manager.isToneInitialized(),
  };
}

// Expose to window for debugging
if (typeof window !== "undefined") {
  (window as any).NotificationSoundManager = NotificationSoundManager;
  (window as any).testNotificationSound = testNotificationSound;
  (window as any).getNotificationSoundStatus = getNotificationSoundStatus;
  (window as any).setNotificationSoundEnabled = setNotificationSoundEnabled;
}
